import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { status, config } = error.response;
        
        // 针对特定状态码进行友好提示
        if (status === 401) {
           // 登录接口的401通常意味着账号密码错误
           if (config?.url?.includes('/auth/login')) {
             message.error('账号或密码错误');
           } else {
             // 其他接口的401意味着token过期，此处通常由 responseInterceptor 处理跳转，
             // 但如果 responseInterceptor 没处理（例如 axios 抛错直接跳过），这里做兜底
             message.error('登录已过期，请重新登录');
           }
        } else if (status === 403) {
          message.error('暂无权限访问该资源');
        } else if (status === 404) {
          message.error('请求的资源不存在');
        } else if (status === 500) {
          message.error('服务器内部错误，请稍后再试');
        } else if (status === 502) {
          message.error('网关错误');
        } else if (status === 503) {
          message.error('服务暂不可用');
        } else if (status === 504) {
          message.error('请求超时');
        } else {
          message.error(`请求失败 (${status})`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，在请求头中添加 token
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return { ...config };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data, status } = response as any;

      // 处理 401 未授权（token 过期或无效）
      if (status === 401 || data?.code === 401) {
        message.error('登录已过期，请重新登录');
        // 清除本地数据
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        // 跳转到登录页
        const currentPath = window.location.pathname;
        if (currentPath !== '/user/login') {
          window.location.href = '/user/login';
        }
        return response;
      }

      // 处理其他错误
      if (data?.success === false) {
        message.error(data?.errorMessage || '请求失败！');
      }

      return response;
    },
  ],
};
